import { useContext } from 'react';
import { StyledStoryWithQuestions } from './StoryWithQuestions.styled';
import useStoryWithQuestions from './useStoryWithQuestions.hook';
import { FullscreenContext } from '@/shared/FullscreenContext/FullscreenContext';
import Button from '@/shared/Button/Button';
import GameAssetsLoader from '@/shared/GameComponents/GameAssetsContext/GameAssetsLoader/GameAssetsLoader';
import TextPage from '../PageTypes/TextPage/TextPage';
import OptionsPage from '../PageTypes/OptionsPage/OptionsPage';
import FillGapsPage from '../PageTypes/FillGapsPage/FillGapsPage';
import DragPage from '../PageTypes/DragPage/DragPage';
import {
  LeftOutlined,
  LoadingOutlined,
  RightOutlined,
} from '@ant-design/icons';
import CoverMenu from '@/shared/GameComponents/UI/CoverMenu/CoverMenu';
import GameButton from '@/shared/GameComponents/UI/GameAnswerButton/GameButton';
import FinalFeedback from '../components/FinalFeedback/FinalFeedback';
import AnimatedStars from '@/shared/StudentPage/StudentPageTopbar/GamificationScoreBoard/AnimatedStars/AnimatedStars';
import useMaximizeScreen from '@/shared/Activities/ActivityComponents/MaximizeScreen/useMaximizeScreen.hook';
import ImagePage from '../PageTypes/ImagePage/ImagePage';

export default function StoryWithQuestions({
  coverImageSrc,
  activity,
  activityConf,
  pages,
  taskProgressActions,
  setCurrentPage,
}) {
  const fullscreenContext = useContext(FullscreenContext);
  const { isMaximized, maximize } = useMaximizeScreen();

  const { state, actions } = useStoryWithQuestions({
    activity,
    activityConf,
    pages,
    taskProgressActions,
    setCurrentPage,
  });

  const paused = state.currentPage && !isMaximized;
  const folder = activity.data.folder;

  const thereIsPrev = state.currentPage ? state.currentPage.id > 1 : false;
  const showPrev =
    taskProgressActions || state.isGamificationStudent
      ? thereIsPrev &&
        pages[(state.currentPage.id ?? 0) - 2].type == 'text' &&
        (state.currentPage.type == 'text' || !state.currentPageFinished)
      : thereIsPrev;

  const thereIsNext = state.currentPage
    ? state.currentPage.id < pages.length
    : false;

  const showNext =
    taskProgressActions || state.isGamificationStudent
      ? thereIsNext &&
        (state.visitedPages?.includes(state.currentPage?.id) ||
          state.currentPageFinished)
      : thereIsNext;

  const showFinish = taskProgressActions
    ? state.currentPageFinished && state.currentPage.id === pages.length
    : state.currentPage?.id === pages.length;

  return (
    <StyledStoryWithQuestions
      className={fullscreenContext.state.isFullscreen ? 'fullscreen' : ''}
    >
      {state.pagesLength ? (
        <>
          {state.currentPage && !paused && !state.finished ? (
            <div className="game-container">
              <PageType
                key={state.currentPage.id}
                page={state.currentPage}
                folder={folder}
                activityActions={actions.shared}
                language={activity?.language}
              />
              <div className="buttons">
                {showPrev ? (
                  <Button
                    className="navButton"
                    type="link"
                    onClick={actions.prevPage}
                  >
                    <div className="icon">
                      <LeftOutlined />
                    </div>
                    <div className="text">Prev</div>
                  </Button>
                ) : (
                  <div />
                )}

                {showNext && (
                  <Button
                    className="navButton"
                    type="link"
                    onClick={actions.nextPage}
                    disabled={state.savingProgress}
                  >
                    <div className="icon">
                      {state.savingProgress ? (
                        <LoadingOutlined />
                      ) : (
                        <RightOutlined />
                      )}
                    </div>
                    <div className="text">Next</div>
                  </Button>
                )}

                {showFinish && (
                  <Button
                    className="navButton"
                    type="link"
                    onClick={actions.finish}
                    disabled={state.savingProgress}
                  >
                    <div className="icon">
                      {state.savingProgress ? (
                        <LoadingOutlined />
                      ) : (
                        <RightOutlined />
                      )}
                    </div>
                    <div className="text">Finish</div>
                  </Button>
                )}
              </div>
              <AnimatedStars originSelector=".correct" />
            </div>
          ) : (
            <CoverMenu activity={activity} coverImageSrc={coverImageSrc}>
              {
                {
                  pause: (
                    <GameButton onClick={() => maximize()} type="primary">
                      Continue
                    </GameButton>
                  ),
                  finish: (
                    <FinalFeedback
                      progress={state.progress}
                      activityConf={activityConf}
                      onRestart={actions.onActivityRestart}
                      questionsCount={state.questionsCount}
                    />
                  ),
                }[state.finished ? 'finish' : 'pause']
              }
            </CoverMenu>
          )}
        </>
      ) : (
        <GameAssetsLoader />
      )}
    </StyledStoryWithQuestions>
  );
}

function PageType(props) {
  return {
    text: <TextPage {...props} />,
    options: <OptionsPage {...props} />,
    fillGaps: <FillGapsPage {...props} />,
    drag: <DragPage {...props} />,
    img: <ImagePage {...props} />,
  }[props.page.type];
}
